import React, {
  Fragment,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { useTheme } from 'styled-components';
import * as S from './CatalogueDrawer.styles';
import { CatalogueListItem } from '@/components/CatalogueListItem/CatalogueListItem';
import { getAdPagePath, sleep } from '@/utils/helpers';
import { Drawer } from '@/components/Drawer/Drawer';
import {
  CategoryWithCountText,
  AdsCountLabel,
} from '@/components/SearchInput/SearchInput.styles';
import { AngleBackIcon, CategoriesIcon } from '@/components/UI/Icons/Icons';
import { ROUTES } from '@/constants/routes';
import { Category, PropertyOption } from '@/api/types/categories.types';
import { findAndGroupCategoriesByParentName } from '@/components/CatalogueDrawer/CatalogueDrawer.helpers';
import { ALL_SHOPS_ID } from '@/components/CatalogueGrid/CatalogueGrid.constants';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { useCategories } from '@/hooks/useCategories';
import { MODAL_ANIMATION_DURATION } from '@/styles/constants';
import {
  SearchInput,
  SearchInputRef,
} from '@/components/SearchInput/SearchInput';

interface CatalogueDrawerProps {
  title?: string;
  isOpen: boolean;
  close: () => void;
  onSelect?: (category: Category, subcategory?: Category | null) => void;
  onPropertySelect?: (
    option: PropertyOption,
    category: Category,
    path: string
  ) => void;
  hasShopsLink?: boolean;
  hasSubcategories?: boolean;
  hasSwitchCheckbox?: boolean;
  isInPortal?: boolean;
  searchPlaceholder?: string;
}

export interface CatalogueDrawerRef {
  reset: () => void;
}

export const CatalogueDrawer = React.forwardRef<
  CatalogueDrawerRef,
  CatalogueDrawerProps
>(
  (
    {
      title,
      isOpen,
      close,
      onSelect,
      onPropertySelect,
      hasShopsLink = true,
      hasSubcategories = true,
      hasSwitchCheckbox = true,
      isInPortal = true,
      searchPlaceholder,
    },
    catalogueDrawerRef
  ) => {
    const { t } = useTranslation();
    const router = useRouter();
    const customPush = useCustomPush();
    const theme = useTheme();
    const { categories, loading } = useCategories(isOpen);
    const [isSubcategoryOpen, setIsSubcategoryOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<null | Category>(
      null
    );
    const [subcategoryId, setSubcategoryId] = useState('');
    const [query, setQuery] = useState('');
    const searchInputRef = useRef<SearchInputRef>(null);

    useEffect(() => {
      searchInputRef.current?.clear();
    }, [isOpen]);

    const filteredSubcategories = useMemo(
      () => Array.from(findAndGroupCategoriesByParentName(query, categories)),
      [query]
    );

    useImperativeHandle(
      catalogueDrawerRef,
      () => ({
        reset: () => {
          setSubcategoryId('');
        },
      }),
      []
    );

    function openSubDrawer(category: Category) {
      setSelectedCategory(category);
      setIsSubcategoryOpen(true);
    }

    async function closeSubDrawer() {
      setIsSubcategoryOpen(false);
      await sleep(500);
      setSelectedCategory(null);
    }

    async function onCategoryClick(category: Category) {
      if (hasSubcategories) {
        openSubDrawer(category);
      } else {
        onSelect?.(category);
        close();
        await closeSubDrawer();
      }
    }

    async function onAllAdsClick() {
      if (!selectedCategory) return;

      close();
      await closeSubDrawer();

      if (onSelect) {
        setSubcategoryId(`${selectedCategory.path}-all`);
        onSelect(selectedCategory, null);
        return;
      }

      customPush(`/${selectedCategory.path}`);
    }

    function getPathWithPropertyOption(
      propertyOption: PropertyOption,
      categoryPath = ''
    ) {
      return `/${categoryPath}?p%5B${propertyOption.propertyLegacyId}%5D=${propertyOption.legacyResourceId}`;
    }

    async function onSubcategoryClick(
      subcategory: Category | null,
      parentCategory?: Category | null
    ) {
      const actualParentCategory = parentCategory || selectedCategory;

      if (!actualParentCategory || !onSelect) return;

      onSelect(actualParentCategory, subcategory);

      close();
      setSubcategoryId(subcategory?.id ?? '');
      await closeSubDrawer();
      await sleep(MODAL_ANIMATION_DURATION);
      setQuery('');
    }

    function isSelectedCategoryPath(path?: string) {
      const currentPath = getAdPagePath(router.query.slug);
      const itemPath = path?.startsWith('/') ? path : `/${path}`;

      return itemPath === currentPath;
    }

    function onSelectFilteredItem(item: Category | PropertyOption) {
      if ('propertyLegacyId' in item) {
        const path = getPathWithPropertyOption(item, item.parentCategory.path);
        onPropertyCheckboxSelect(item, path, item.parentCategory);
        return;
      }

      if (onSelect) {
        onSubcategoryClick(item, item.parent);
        return;
      }

      customPush(`/${item.path}`);
    }

    function isSelectedSubcategory(subcategory: Category | PropertyOption) {
      if ('propertyLegacyId' in subcategory) return false;

      return subcategoryId
        ? subcategory.id === subcategoryId
        : isSelectedCategoryPath(subcategory.path);
    }

    const isAllAdsSelected = subcategoryId
      ? subcategoryId === `${selectedCategory?.path}-all`
      : isSelectedCategoryPath(selectedCategory?.path);

    async function onPropertyCheckboxSelect(
      item: PropertyOption,
      path: string,
      category = selectedCategory
    ) {
      close();
      await closeSubDrawer();

      if (onPropertySelect && category) {
        onPropertySelect(item, category, path);
      } else {
        customPush(path);
      }
    }

    function renderCommonCheckbox(
      item: Category | PropertyOption,
      path: string
    ) {
      const key = 'id' in item ? item.id : item.legacyResourceId;
      return (
        <S.CategoryCheckbox
          onChangeHook={() =>
            onPropertyCheckboxSelect(item as PropertyOption, path)
          }
          key={key}
          label={item.name}
          appearance="radio"
          size="lg"
          hasSwitch={hasSwitchCheckbox}
        />
      );
    }

    function renderCategoryCheckbox(item: Category) {
      const path = `/${item.path}`;
      return onSelect ? (
        <S.CategoryCheckbox
          onChangeHook={() => onSubcategoryClick(item)}
          key={item.id}
          value={isSelectedSubcategory(item)}
          label={item.name}
          appearance="radio"
          size="lg"
          hasSwitch={hasSwitchCheckbox}
        />
      ) : (
        renderCommonCheckbox(item, path)
      );
    }

    function renderPropertyCheckbox(item: PropertyOption) {
      const path = getPathWithPropertyOption(item, selectedCategory?.path);
      return renderCommonCheckbox(item, path);
    }

    return (
      <Drawer
        dataTestId="catalogue-drawer"
        isOpen={isOpen}
        close={close}
        title={title ?? t('catalogue.catalogue')}
        isInPortal={isInPortal}
        isLoading={loading}
        background={theme.palette.primary.background}
        hasBottomBorder={false}
        hasBottomMargin={false}
        subtitle={
          onSelect && (
            <S.Container>
              <SearchInput
                ref={searchInputRef}
                onSearch={setQuery}
                placeholder={searchPlaceholder}
                leftIcon={
                  <CategoriesIcon fill={theme.palette.tertiary.labels} />
                }
                hasPadding={false}
                hasBorderBottom={false}
                customPaddingTop={12}
                customPaddingBottom={12}
              />
            </S.Container>
          )
        }
      >
        {query ? (
          filteredSubcategories.map(([parentName, subcategories]) => {
            return (
              <Fragment key={parentName}>
                <S.CategoryGroupName>{parentName}</S.CategoryGroupName>
                <S.Container>
                  {subcategories.map(item => {
                    const key =
                      'legacyResourceId' in item
                        ? item.legacyResourceId
                        : item.id;
                    return (
                      <S.CategoryCheckbox
                        onChangeHook={() => onSelectFilteredItem(item)}
                        key={key}
                        value={isSelectedSubcategory(item)}
                        label={
                          <CategoryWithCountText>
                            {item.name}
                            {'count' in item ? (
                              <AdsCountLabel>({item.count ?? 0})</AdsCountLabel>
                            ) : null}
                          </CategoryWithCountText>
                        }
                        appearance="radio"
                        size="lg"
                        hasSwitch={hasSwitchCheckbox}
                        hasBorderBottom
                      />
                    );
                  })}
                </S.Container>
              </Fragment>
            );
          })
        ) : (
          <>
            <S.Container>
              {loading
                ? Array.from({ length: 14 }, (_, index) => (
                    <CatalogueListItem key={index} isLoading />
                  ))
                : categories?.map(item => {
                    return (
                      <CatalogueListItem
                        key={item.id}
                        onClick={() => onCategoryClick(item)}
                        item={item}
                        isLoading={loading}
                      />
                    );
                  })}

              {hasShopsLink && (
                <CatalogueListItem
                  data-stat="catalog-category-shop"
                  item={
                    {
                      id: ALL_SHOPS_ID,
                      icon: 'shop.png',
                      name: t('catalogue.stores'),
                      path: ROUTES.shops,
                    } as Category
                  }
                  isLoading={loading}
                />
              )}
            </S.Container>

            <Drawer
              dataTestId="catalogue-subdrawer"
              isOpen={isSubcategoryOpen}
              title={selectedCategory?.name}
              left={{ icon: <AngleBackIcon />, onClick: closeSubDrawer }}
              background={theme.palette.primary.background}
              hasBottomBorder={false}
              hasBottomMargin={false}
            >
              <S.Container>
                <S.CategoryCheckbox
                  onChangeHook={onAllAdsClick}
                  value={isAllAdsSelected}
                  label={t('catalogue.all_ads')}
                  appearance="radio"
                  size="lg"
                  hasSwitch={hasSwitchCheckbox}
                />

                {selectedCategory?.children.length
                  ? selectedCategory.children.map(renderCategoryCheckbox)
                  : selectedCategory?.propertyOptions?.map(
                      renderPropertyCheckbox
                    )}
              </S.Container>
            </Drawer>
          </>
        )}
      </Drawer>
    );
  }
);

CatalogueDrawer.displayName = 'CatalogueDrawer';
